import { ERROR_VENDOR_SELECTED_NOT_AVAILABLE, SENTRY_ERRORS } from "@/utils";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";

const checkTimeExpiration = (timestamp: number, timeToLive: number) => {
  const now = Date.now();
  return now > timestamp + timeToLive;
};

export const updateUserSelectionsStorage = ({ commit }) => {
  commit("updateUserSelectionsStorage");
};

export const updateIWSettingsTimeStamp = ({ commit }) => {
  commit("updateIWSettingsTimeStamp");
};

export const updateVenueTimeStamp = ({ commit }) => {
  commit("updateVenueTimeStamp");
};

export const updateVendorTimeStamp = ({ commit }) => {
  commit("updateVendorTimeStamp");
};

export const updateItemsTimeStamp = ({ commit }) => {
  commit("updateItemsTimeStamp");
};

export const cleanExpiredData = async ({ dispatch, commit, getters }, venueId) => {
  if (getters.isUserSelectionExpired) {
    // this will clear cart, logout user, and clean favorites.
    dispatch("user/logOutUser", null, { root: true });

    // cart
    commit("cart/resetState", null, { root: true });
    commit("cart/resetCheckoutState", null, { root: true });
    commit("cart/resetDiscountState", null, { root: true });
    commit("cart/resetOAPIState", null, { root: true });

    // vendor (just the user selectable properties)
    commit("vendor/resetActiveVendor", null, { root: true });
    commit("vendor/resetMenuItemsFilterState", null, { root: true });

    // venue (just the deepLinked flag)
    commit("venue/setWasDeepLinkedUsed", null, { root: true });

    // delivery
    commit("delivery/resetState", null, { root: true });

    // level (just the user selectable properties)
    commit("level/resetActiveLevelSelection", null, { root: true });

    // order ahead
    commit("orderAhead/resetState", null, { root: true });

    // user
    commit("user/resetState", null, { root: true });
    commit("user/resetFavoritesState", null, { root: true });
  }

  //Clear all server data if any of the timestamps is expired
  const fetchServer: boolean =
    getters.isIwSettingsTimeStampExpired ||
    getters.isVenueTimeStampExpired ||
    getters.isVendorTimeStampExpired ||
    getters.isItemsTimeStampExpired;

  if (fetchServer) {
    try {
      await dispatch("updateServerConfig", venueId);
    } catch (error: any) {
      errorLoggerAdapter.createIssue(
        SENTRY_ERRORS.ERROR_UPDATING_SERVER_CONFIG,
        "StorageMaintenance",
        "cleanExpiredData",
        {
          description: JSON.stringify(error)
        },
        error
      );
    }
  }

  return Promise.resolve();
};

export const updateVenueConfig = async ({ dispatch, rootGetters, commit }, venue) => {
  await dispatch("venueSettings/updateInteractWebSettings", venue, { root: true });
  await dispatch("venue/updateActiveVenue", venue, { root: true });
  await dispatch("vendor/detectClosedVendors", null, { root: true });

  const activeVendorId = (rootGetters && rootGetters["vendor/getActiveVendorId"]) || "";
  const vendor = (rootGetters && rootGetters["vendor/getVendorByID"](activeVendorId)) || {};

  if ((vendor?.status && vendor?.isOpen) || !activeVendorId) return;

  await dispatch("cart/clearShoppingCart", null, { root: true });
  commit("vendor/resetActiveVendor", null, { root: true });
  return Promise.reject({ errorMessage: ERROR_VENDOR_SELECTED_NOT_AVAILABLE });
};

export const updateServerConfig = async ({ dispatch, rootGetters, commit }, venueId) => {
  try {
    await dispatch("updateVenueConfig", venueId);
  } catch (error) {
    return Promise.reject(error);
  }

  const activeVendor = rootGetters["vendor/getActiveVendor"];
  if (!activeVendor) return;

  //if seats are active and selection match then get vendor available for that selection
  if (
    (rootGetters["venueSettings/seatRequired"] && rootGetters["delivery/matchesCurrentSeatSelection"]) ||
    rootGetters["venue/getWasDeepLinkedUsed"]
  ) {
    const levelId = rootGetters["level/getActiveLevelId"];
    const sectionId = rootGetters["level/getActiveSectionId"];
    const rowId = rootGetters["level/getActiveRowId"];
    const seatId = rootGetters["level/getActiveSeatId"] || "";

    await dispatch(
      "vendor/fetchLocationVendors",
      {
        venueId,
        levelId,
        sectionId,
        rowId,
        seatId
      },
      { root: true }
    );
  }

  await dispatch("vendor/detectClosedVendors", null, { root: true });
  const vendor = rootGetters["vendor/getVendorByID"](activeVendor.id);

  if (!vendor?.status || !vendor?.isOpen) {
    commit("vendor/resetActiveVendor", null, { root: true });
    await dispatch("cart/clearShoppingCart", null, { root: true });
    return Promise.reject({ errorMessage: ERROR_VENDOR_SELECTED_NOT_AVAILABLE });
  }

  commit("vendor/setActiveVendor", vendor, { root: true });
  dispatch("vendor/fetchVendorPickupTimes", { vendorId: vendor.id }, { root: true });

  if (vendor.hideMenu) {
    commit("vendor/setVendorMenu", [], { root: true });
    await Promise.all([
      dispatch("vendor/fetchSubvendorData", { vendorId: vendor.id, venueId }, { root: true }),
      dispatch("dayParts/setVenueDayParts", null, { root: true }),
      dispatch("cart/reviewShoppingCart", null, { root: true }),
      dispatch("vendor/setAllItemsAttributes", null, { root: true })
    ]);
  } else {
    await dispatch("vendor/updateActiveVendorMenu", activeVendor.id, { root: true });
  }

  dispatch("vendor/showDisabledItemsModal", null, { root: true });
};

export const updateServerConfigWithMinimumTimePassed = async ({ dispatch, rootGetters, state }, venue) => {
  const minimumTimeToFetch = parseInt(process.env.VUE_APP_MINIMUM_TIME_BETWEEN_SERVER_UPDATE || "");
  const oldestServerConfigTimeStamp = Math.min(
    state.iwSettingsTimeStamp,
    state.venueTimeStamp,
    state.vendorTimeStamp,
    state.itemsTimeStamp
  );

  if (checkTimeExpiration(oldestServerConfigTimeStamp, minimumTimeToFetch)) {
    try {
      await dispatch("updateServerConfig", venue || rootGetters["venue/getActiveVenue"]);
    } catch (error) {
      return Promise.reject(error);
    }
  }
};
